import React from "react"
import { Modal, Form, Col, Button, Row, Table, Spinner } from "react-bootstrap"
import { toast } from "react-toastify"
import { Subscription } from "rxjs"
import LabAccount from "../../api/Administration/Model/LabAccount"
import { APIError } from "../../api/ObservableFromFetch"
import GovDevice, { ModifyGovDevice } from "../../api/Randoms/GovPoolModel/GovDevice"
import { DevicesGetObservable, GovDeviceModifyObservable } from "../../api/Randoms/GovDevicesGet"
import LabAccountListGet from "../../api/Administration/LabAccountListGet"
import { Typeahead } from "react-bootstrap-typeahead"
import { FaAngleDown } from "react-icons/fa"
import DeviceModal from "./DeviceModal"
import ConfirmationModal from "../ConfirmationModal"

interface DeviceSelectionProps {
  portalMode?: boolean
  implementationID: number
  accountID: number
  onClose: () => void
}

interface  DeviceSelectionState {
  addNewDevice: boolean
  labAccounts: LabAccount[]
  devices: GovDevice[]
  selectedLab?: LabAccount
  loading: boolean
  deviceDetails: boolean
  details?: GovDevice
  confirmModal: boolean
}

export default class  DeviceSelection extends React.PureComponent< DeviceSelectionProps,  DeviceSelectionState> {
  labAccountSubscription?: Subscription
  deviceSubscription?: Subscription
  deleteSubscription?: Subscription
  state:  DeviceSelectionState = {
    addNewDevice: false,
    labAccounts: [],
    devices: [],
    loading: false,
    deviceDetails: false,
    confirmModal: false
  }

  labAccountRef = React.createRef<Typeahead<LabAccount>>()

  componentWillMount() {
    this.loadLabAccounts()
  }

  componentWillUnmount() {
    this.labAccountSubscription && this.labAccountSubscription.unsubscribe()
    this.deviceSubscription && this.deviceSubscription.unsubscribe()
    this.deleteSubscription && this.deleteSubscription.unsubscribe()
  }

  onCancel = () => {
    this.props.onClose()
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  loadLabAccounts = () => {
    this.setState({ loading: true })
    this.deviceSubscription = LabAccountListGet(this.props.implementationID, this.props.accountID, false).subscribe(
      data => this.setState({ loading: false, labAccounts: [...data.ActiveLabAccount.filter(({LabAccountNumber}) => LabAccountNumber.split(".")[1].includes("CON") || LabAccountNumber.split(".")[1].includes("OCO"))] }),
      error => this.handleAPIError(error, { loading: false })
    )
  }

  loadDevices = (labAccount: string) => {
    this.setState({ loading: true })
    this.deviceSubscription = DevicesGetObservable(labAccount).subscribe(
      data => this.setState({ loading: false, devices: data }),
      error => this.handleAPIError(error, { loading: false })
    )
  }

  handleLabChange = (items: LabAccount[]) => {
    const selectedLab = items[0]
    this.setState({ selectedLab: selectedLab, devices: [] }, () => {
      if (!!selectedLab) this.loadDevices(selectedLab.LabAccountNumber)
    })
  }

  hideModal = () => this.setState({ confirmModal: false, details: undefined })

  confirmDelete = () => {
    const { details, selectedLab } = this.state
    const data: ModifyGovDevice = {
      DeviceID: details?.DeviceID || 0,
      LabAccount: selectedLab?.LabAccountNumber || "",
      DeviceLabAccountXrefID: details?.DeviceLabAccountXrefID || 0,
      Delete: true
    }
    this.hideModal()
    this.deleteSubscription = GovDeviceModifyObservable(data).subscribe(
      data => {
        toast.success(data)
        this.handleLabChange(!!this.state.selectedLab ? [this.state.selectedLab] : [])
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }

  showDeviceDetailsModal = (data: GovDevice) => this.setState({ deviceDetails: true, details: data })

  onSave = () => {
    this.setState({addNewDevice: false, deviceDetails: false, details: undefined})
    this.handleLabChange(!!this.state.selectedLab ? [this.state.selectedLab] : [])
  }

  dataRows = (data: GovDevice[]) => data.map((row: GovDevice, i: number) => this.dataRow(row, i))

  dataRow = (data: GovDevice, index: number) => {
    return <tr key={index}>
      <td className="align-middle"><Button variant="underline-link" onClick={() => this.showDeviceDetailsModal(data)}>{data.Description}</Button></td>
      <td className="align-middle">{data.Code}</td>
      <td className="align-middle">{data.SampleType}</td>
      <td className="align-middle"><Button variant="underline-link" onClick={() => this.setState({ confirmModal: true, details: data})}>Delete</Button></td>
    </tr>
  }

  devicePanelCodes = () =>
    this.state.devices.reduce((res, el) => {
      const key = el.DeviceID
      res[key] = res[key] || []
      res[key].push(el.PanelCode)
      return res
    }, Object.create(null))

  render() {
    const { loading, addNewDevice, labAccounts, devices, selectedLab, deviceDetails } = this.state

    return (
      <>
        <Modal backdrop="static" show size="lg">
          <Modal.Header>
            <Modal.Title className="text-uppercase">Devices</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Row>
              <Col className="pb-4">
                <Typeahead id="labAccount"
                  ref={this.labAccountRef}
                  labelKey="LabAccountNumber"
                  emptyLabel="No Laboratory Accounts Found"
                  disabled={loading}
                  filterBy={(option, props) => props.selected.length > 0 || option.LabAccountNumber.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                  onChange={this.handleLabChange}
                  options={labAccounts || []}
                  selected={selectedLab ? [selectedLab] : []}
                  placeholder="Enter the Lab Account">
                  <Button variant="toggle"
                    disabled={loading}
                    onClick={() => { this.labAccountRef.current?.toggleMenu() }}>
                    <FaAngleDown />
                  </Button>
                </Typeahead>
              </Col>
            </Form.Row>
            {!!selectedLab && <Form.Group as={Row}>
              <Table responsive bordered className="dark text-center">
                <thead>
                  <tr>
                    <th className="w-25">Device</th>
                    <th className="w-25">Code</th>
                    <th className="w-25">Sample Type</th>
                    <th className="w-25">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <><tr><td colSpan={4}><Spinner animation="border" size="sm" /></td></tr></>}
                  {!loading && devices.length === 0 && <><tr><td colSpan={4}>NO DATA FOUND</td></tr></>}
                  {this.dataRows(devices)}
                </tbody>
              </Table>
            </Form.Group>}
            {deviceDetails && <DeviceModal onHide={() => this.setState({ addNewDevice: false, deviceDetails: false, details: undefined })} 
              addNew={addNewDevice}
              currentDevices={devices.map(el => el.DeviceID)}
              labAccount={selectedLab}
              details={this.state.details}
              onSave={() => this.onSave()} />}
            <ConfirmationModal show={this.state.confirmModal}
                title="CONFIRM REMOVE"
                textTitle="Are you sure you want to remove selected record?"
                text="Selected record will be removed permanently."
                onConfirm={this.confirmDelete}
                onHide={this.hideModal} />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.onCancel} className="px-4">Cancel</Button>
            <Button onClick={() => this.setState({ addNewDevice: true, deviceDetails: true })} className="px-4" disabled={!selectedLab}>Add New Device</Button>
          </Modal.Footer>
        </Modal>
      </>
    )
  }

}
